/**
 * API client – fetch wrapper with base URL and optional auth token.
 * APK: Capacitor patches fetch() when CapacitorHttp.enabled is true in config,
 * so we always use fetch() and the native HTTP client is used automatically.
 */
const BACKEND_LAN = 'http://192.168.1.90:4000';
const BASE_URL =
  process.env.NODE_ENV === 'development' && typeof window !== 'undefined'
    ? window.location.origin
    : (process.env.REACT_APP_API_URL || BACKEND_LAN);

if (typeof window !== 'undefined') {
  console.log('[API] BASE_URL=', BASE_URL);
}

const getToken = (): string | null => {
  return localStorage.getItem('adminToken');
};

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

/** Normalize any thrown error into an ApiError-like Error with a clear message */
function toApiError(err: unknown, url: string): Error & ApiError {
  const e = err as Error & { status?: number; code?: string };
  const message =
    (e?.message && String(e.message).trim()) ||
    (e && typeof e === 'object' && 'toString' in e ? String(e.toString()) : 'Network request failed');
  const out = new Error(message) as Error & ApiError;
  out.status = e?.status;
  out.code = e?.code;
  return out;
}

async function request<T>(
  path: string,
  options: RequestInit & { params?: Record<string, string> } = {}
): Promise<T> {
  const { params, ...init } = options;
  const url = new URL(path, BASE_URL);
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  }
  const token = getToken();
  const isFormData =
    typeof FormData !== 'undefined' && init.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string>),
  };
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const fullUrl = url.toString();

  let res: Response;
  try {
    res = await fetch(fullUrl, { ...init, headers });
  } catch (err) {
    const apiErr = toApiError(err, fullUrl);
    console.error('[API] Request failed', { url: fullUrl, message: apiErr.message, err });
    throw apiErr;
  }
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // ignore
    }
  }
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/auth/login')) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('adminUser');
      window.location.href = '/login';
    }
    const message =
      (body && typeof (body as { error?: string }).error === 'string'
        ? (body as { error: string }).error
        : null) || res.statusText || `Request failed (${res.status})`;
    const err = new Error(message) as Error & ApiError;
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return (body ?? undefined) as T;
}

export const apiClient = {
  get: <T>(path: string, params?: Record<string, string>) =>
    request<T>(path, { method: 'GET', params }),

  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),

  postForm: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', body: formData }),

  put: <T>(path: string, body: unknown, params?: Record<string, string>) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body), params }),

  delete: (path: string) => request<void>(path, { method: 'DELETE' }),
};

export { BASE_URL, getToken };
